import { useState, useEffect, useRef, createContext, useContext, useCallback, useMemo } from 'react';
import { Language } from '../../App';

const ToastContext = createContext();

const toTitleCase = (phrase) => {
  if (!phrase) return "";
  return phrase
    .trim()
    .replace(/[._-]+/g, " ")
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const KNOWN_TOAST_MAP = [
  // Reports
  {
    pattern: /report.*(?:submitted|success)|reportad[oa] exitosamente/i,
    title: "Report Submitted",
    subText: "Your report has been sent and is under review.",
  },
  {
    pattern: /failed.*(?:report)|error.*(?:report)/i,
    title: "Report Failed",
    subText: "Unable to submit your report. Please try again.",
  },
  {
    pattern: /select a reason for reporting/i,
    title: "Category Required",
    subText: "Please choose a category before submitting.",
  },
  {
    pattern: /enter a (?:reason|report)/i,
    title: "Details Required",
    subText: "Please provide a reason explaining your report.",
  },

  // Posts & Feeds
  {
    pattern: /post.*(?:upload|publish|creat).*(?:success|exitos)/i,
    title: "Post Published",
    subText: "Your post is now live and visible on your feed.",
  },
  {
    pattern: /post.*(?:updat|edit).*(?:success|exitos)/i,
    title: "Post Updated",
    subText: "Your changes have been saved to the post.",
  },
  {
    pattern: /post.*(?:delet|elimin).*(?:success|exitos)/i,
    title: "Post Deleted",
    subText: "Your post was successfully removed.",
  },
  {
    pattern: /repost.*(?:success|reposted)/i,
    title: "Post Reposted",
    subText: "Shared to your feed for your followers.",
  },
  {
    pattern: /repost.*(?:removed|elimin)/i,
    title: "Repost Removed",
    subText: "This post was removed from your profile feed.",
  },
  {
    pattern: /poll.*(?:added|created).*(?:success|exitos)/i,
    title: "Poll Created",
    subText: "Your poll is active and open for votes.",
  },
  {
    pattern: /comment.*(?:deleted|elimin)/i,
    title: "Comment Deleted",
    subText: "Your comment was successfully removed.",
  },

  // Sessions in Agenda & Clinical
  {
    pattern: /session.*(?:updated|success)/i,
    title: "Session Updated",
    subText: "Timeline session details were saved successfully.",
  },
  {
    pattern: /session.*added/i,
    title: "Session Added",
    subText: "New session added to your agenda timeline.",
  },
  {
    pattern: /session.*(?:deleted|removed)/i,
    title: "Session Removed",
    subText: "The session was removed from the agenda timeline.",
  },

  // Speakers
  {
    pattern: /speaker.*(?:updated|success)/i,
    title: "Speaker Updated",
    subText: "Speaker information has been updated.",
  },
  {
    pattern: /speaker.*added/i,
    title: "Speaker Added",
    subText: "Speaker added to the featured speakers list.",
  },
  {
    pattern: /speaker.*removed/i,
    title: "Speaker Removed",
    subText: "Speaker was removed from this event.",
  },

  // Events & Registration
  {
    pattern: /event.*(?:joined|enrolled|registered)|seat reserved|unido al evento/i,
    resolve: (str, lang) => ({
      title: lang?.event_joined_title || "Registration Confirmed",
      subText: lang?.event_joined_desc || "You are registered! Added to your schedule.",
    }),
  },
  {
    pattern: /(?:reservation|registration).*cancelled|event[_\s]+left|leave.*event|reserva.*cancelada|salida.*(?:del\s+)?evento/i,
    resolve: (str, lang) => {
      const isEvent = /event/i.test(str);
      return {
        title: isEvent
          ? (lang?.event_left_title || "Event Left")
          : (lang?.reservation_cancelled_title || lang?.event_left_title || "Registration Cancelled"),
        subText: lang?.event_left_desc || "Your spot has been successfully released.",
      };
    },
  },
  {
    pattern: /event.*deleted|evento.*eliminad/i,
    resolve: (str, lang) => ({
      title: lang?.event_deleted_title || "Event Deleted",
      subText: lang?.event_deleted_desc || "The event was removed from your agenda.",
    }),
  },
  {
    pattern: /webinar.*deleted|seminario.*eliminad/i,
    resolve: (str, lang) => ({
      title: lang?.webinar_deleted_title || "Webinar Deleted",
      subText: lang?.webinar_deleted_desc || "The webinar was removed from your agenda.",
    }),
  },
  {
    pattern: /clinical session.*deleted|sesi[oó]n.*(?:cl[íi]nica)?.*eliminad/i,
    resolve: (str, lang) => ({
      title: lang?.session_deleted_title || "Session Deleted",
      subText: lang?.session_deleted_desc || "The clinical session was deleted successfully.",
    }),
  },

  // Profile & Account Settings
  {
    pattern: /profile.*(?:updated|guardad)/i,
    title: "Profile Updated",
    subText: "Your profile information has been saved.",
  },
  {
    pattern: /password.*(?:changed|reset).*(?:success|exitos)/i,
    title: "Password Updated",
    subText: "Your password has been changed securely.",
  },
  {
    pattern: /hospital.*(?:added|agregad)/i,
    title: "Hospital Added",
    subText: "Affiliated hospital details were saved.",
  },
  {
    pattern: /hospital.*(?:deleted|removed|eliminad)/i,
    title: "Hospital Removed",
    subText: "Affiliated hospital was removed from your profile.",
  },
  {
    pattern: /ticket.*(?:resolved|closed)/i,
    title: "Ticket Resolved",
    subText: "Your support ticket has been closed.",
  },

  // Documents & Courses & Mentorship
  {
    pattern: /document.*(?:uploaded|submitted)/i,
    title: "Document Uploaded",
    subText: "Your document was uploaded successfully.",
  },
  {
    pattern: /document.*(?:deleted|eliminad)/i,
    title: "Document Removed",
    subText: "The document was removed successfully.",
  },
  {
    pattern: /course.*saved/i,
    title: "Course Saved",
    subText: "Course added to your saved learning list.",
  },
  {
    pattern: /question.*submitted/i,
    title: "Question Sent",
    subText: "Your question has been sent to the speaker.",
  },
  {
    pattern: /mentorship.*completed/i,
    title: "Mentorship Completed",
    subText: "Mentorship session marked as completed.",
  },
  {
    pattern: /shared.*successfully/i,
    title: "Content Shared",
    subText: "Shared successfully with your network.",
  },

  // Invitations
  {
    pattern: /invitation.*(?:sent|success)|invitaci[oó]n.*enviad/i,
    resolve: (str, lang) => {
      const countMatch = str.match(/(\d+)/);
      const count = countMatch ? parseInt(countMatch[1], 10) : 1;
      const title =
        count > 1
          ? (lang?.invitations_sent_title || `${count} Invitations Sent`).replace("{count}", count)
          : (lang?.invitation_sent_title || "1 Invitation Sent");
      const subText =
        count > 1
          ? (lang?.invitations_sent_desc || `${count} invitations have been sent successfully.`).replace("{count}", count)
          : (lang?.invitation_sent_desc || "Invitation has been sent successfully.");
      return {
        title,
        subText,
      };
    },
  },

  // Restrictions & Live
  {
    pattern: /cannot be edited after.*(?:live|ended)/i,
    title: "Edit Restricted",
    subText: "Sessions that are live or ended cannot be edited.",
  },
  {
    pattern: /cannot be deleted after.*(?:live|ended)/i,
    title: "Deletion Restricted",
    subText: "Live sessions cannot be deleted while ongoing.",
  },
  {
    pattern: /meeting.*link.*not available|session room is not active yet|sala de sesiones a[uú]n no est[aá] activa/i,
    resolve: (str, lang) => ({
      title: lang?.notice || "Notice",
      subText: lang?.meeting_link_not_available || "Session room is not active yet. Please wait for the host.",
    }),
  },
  {
    pattern: /webinar.*ended/i,
    title: "Session Ended",
    subText: "This webinar session has already concluded.",
  },
  {
    pattern: /copied to clipboard/i,
    title: "Link Copied",
    subText: "Link has been copied to your clipboard.",
  },
  {
    pattern: /please log in/i,
    title: "Login Required",
    subText: "Please log in to your account to continue.",
  },
  {
    pattern: /not allowed in your current subscription/i,
    title: "Upgrade Required",
    subText: "This action is not included in your current subscription.",
  },
  {
    pattern: /payment.*(?:cancelled|not authorized)/i,
    title: "Payment Incomplete",
    subText: "Payment authorization was not completed.",
  },
  // Host updates (webinar / session / event hosts)
  {
    pattern: /host.*(?:updated|status)|user.*(?:added|removed|made).*as host|usuario.*(?:eliminado|añadido|agregado).*como anfitri[oó]n|anfitri[oó]n.*actualizad/i,
    resolve: (str, lang) => {
      const isRemoved = /remov|elimin|quitar/i.test(str);
      const isAdded = /add|agreg|añad|made/i.test(str);
      return {
        title: lang?.host_updated_title || "Host Updated",
        subText: isRemoved
          ? (lang?.host_removed_desc || "User removed as host for Webinar")
          : isAdded
            ? (lang?.host_added_desc || "User added as host for Webinar")
            : (lang?.host_updated_desc || "Host status updated successfully."),
      };
    },
  },
];

const resolveToastContent = (rawMessage, rawTitle, type, lang) => {
  const str = String(rawMessage || "").trim();
  const titleStr = String(rawTitle || "").trim();

  // If explicit title is provided, check if it or the message matches a known localized toast pattern first
  if (titleStr) {
    const combinedStr = `${titleStr} ${str}`;
    for (const item of KNOWN_TOAST_MAP) {
      if (item.pattern.test(combinedStr) || item.pattern.test(titleStr) || item.pattern.test(str)) {
        if (typeof item.resolve === "function") {
          return item.resolve(str || titleStr, lang);
        }
        return {
          title: item.title,
          subText: item.subText,
        };
      }
    }
    return {
      title: titleStr,
      subText: str,
    };
  }

  // 1. Quoted entity patterns (e.g. '"vitae Consequuntur D" updated successfully')
  const quotedMatch = str.match(/^["'“]([^"'”]+)["'”]\s*(.*)$/);
  if (quotedMatch) {
    const entityName = quotedMatch[1].trim();
    const actionPart = (quotedMatch[2] || "").toLowerCase();

    // Determine entity type from actionPart or str
    let updatedTitle = lang?.event_updated || "Event Updated";
    let createdTitle = lang?.event_created || "Event Created";
    let deletedTitle = lang?.event_deleted || "Deleted successfully";

    if (/clinical.*session|session/i.test(str)) {
      updatedTitle = lang?.clinical_session_updated || lang?.session_updated || "Session Updated";
      createdTitle = lang?.clinical_session_created || "Session Created";
      deletedTitle = lang?.session_deleted || "Session Deleted";
    } else if (/webinar/i.test(str)) {
      updatedTitle = lang?.webinar_updated || "Webinar Updated";
      createdTitle = lang?.webinar_created || "Webinar Created";
    } else if (/speaker|ponente/i.test(str)) {
      updatedTitle = lang?.speaker_updated_success || "Speaker Updated";
      createdTitle = lang?.speaker_added_success || "Speaker Added";
      deletedTitle = lang?.speaker_removed || "Speaker Removed";
    } else if (/post|publicaci[oó]n/i.test(str)) {
      updatedTitle = lang?.post_updated_successfully || "Post Updated";
    }

    if (/updat|edit|save|modifi|actualiz/i.test(actionPart)) {
      return {
        title: updatedTitle,
        subText: (lang?.item_updated_success || `"${entityName}" updated successfully!`).replace("{name}", entityName),
      };
    }
    if (/add|creat|agreg/i.test(actionPart)) {
      return {
        title: createdTitle,
        subText: (lang?.item_created_success || `"${entityName}" created successfully!`).replace("{name}", entityName),
      };
    }
    if (/delet|remov|elimin/i.test(actionPart)) {
      return {
        title: deletedTitle,
        subText: (lang?.item_deleted_success || `"${entityName}" was removed successfully.`).replace("{name}", entityName),
      };
    }
    return {
      title: updatedTitle,
      subText: (lang?.item_updated_success || `"${entityName}" updated successfully!`).replace("{name}", entityName),
    };
  }

  // 2. Check known patterns in KNOWN_TOAST_MAP
  for (const item of KNOWN_TOAST_MAP) {
    if (item.pattern.test(str)) {
      if (typeof item.resolve === "function") {
        return item.resolve(str, lang);
      }
      return {
        title: item.title,
        subText: item.subText,
      };
    }
  }

  // 3. Dynamic formatting fallback
  if (type === "success") {

    if (/sent|enviad/i.test(str)) {
      return {
        title: "Sent Successfully",
        subText: str.endsWith(".") ? str : `${str}.`,
      };
    }

    // If string describes an update, addition, or removal
    if (/updated|guardad/i.test(str)) {
      return {
        title: lang?.changes_saved || "Changes Saved",
        subText: str.endsWith(".") ? str : `${str}.`,
      };
    }
    if (/added|agregad/i.test(str)) {
      return {
        title: "Item Added",
        subText: str.endsWith(".") ? str : `${str}.`,
      };
    }
    if (/deleted|removed|eliminad/i.test(str)) {
      return {
        title: "Item Removed",
        subText: str.endsWith(".") ? str : `${str}.`,
      };
    }

    // Strip redundant trailing phrases like "successfully", "con éxito", "correctamente"
    const cleanStr = str
      .replace(/successfully\.?!?/gi, "")
      .replace(/success\.?!?/gi, "")
      .replace(/correctamente\.?!?/gi, "")
      .replace(/con [eé]xito\.?!?/gi, "")
      .replace(/exitosamente\.?!?/gi, "")
      .replace(/["'“”]/g, "")
      .trim();

    const wordCount = cleanStr.split(/\s+/).filter(Boolean).length;
    if (cleanStr && wordCount <= 3 && cleanStr.length <= 25) {
      const title = toTitleCase(cleanStr);
      let subText = str.endsWith(".") ? str : `${str}.`;
      if (/update|change|edit|save/i.test(cleanStr)) {
        subText = "Your changes have been saved successfully.";
      }
      return {
        title,
        subText,
      };
    }
    return {
      title: lang?.changes_saved || "Changes Saved",
      subText: str || "Your changes were saved successfully.",
    };
  }

  if (type === "warning") {
    return {
      title: lang?.attention_required || "Attention Required",
      subText: str,
    };
  }

  if (type === "info") {
    return {
      title: lang?.notice || "Notice",
      subText: str,
    };
  }

  // Error fallback
  const isFailedPrefix = /^failed to /i.test(str);
  if (isFailedPrefix) {
    const actionPart = str.replace(/^failed to /i, "").trim();
    const title = toTitleCase(actionPart) + " " + (lang?.failed || "Failed");
    return {
      title,
      subText: str,
    };
  }

  if (/something went wrong/i.test(str)) {
    return {
      title: lang?.action_failed || "Action Failed",
      subText: str,
    };
  }

  return {
    title: lang?.action_failed || "Action Failed",
    subText: str || "Something went wrong. Please try again.",
  };
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});
  const { lang } = useContext(Language) || {};

  const removeToast = useCallback((id) => {
    // Start slide-out animation
    setToasts(prev =>
      prev.map(t => (t.id === id ? { ...t, isVisible: false } : t))
    );
    // Clear auto-dismiss timer if still running
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    // Remove from DOM after animation completes
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  }, []);

  const showToast = useCallback((firstArg, secondArg, thirdArg, fourthArg) => {
    let title = "";
    let message = "";
    let type = "error";
    let duration = 3000;

    if (typeof firstArg === "object" && firstArg !== null) {
      // Object signature: showToast({ title, message, type, duration })
      title = firstArg.title || "";
      message = firstArg.message || firstArg.description || firstArg.subText || "";
      type = firstArg.type || "success";
      duration = firstArg.duration || 3000;
    } else if (
      typeof firstArg === "string" &&
      typeof secondArg === "string" &&
      (thirdArg === "success" || thirdArg === "error" || thirdArg === "warning" || thirdArg === "info")
    ) {
      // 4-arg signature: showToast(title, message, type, duration)
      title = firstArg;
      message = secondArg;
      type = thirdArg;
      duration = typeof fourthArg === "number" ? fourthArg : 3000;
    } else {
      // Standard signature: showToast(message, type, duration)
      message = String(firstArg || "");
      type = typeof secondArg === "string" ? secondArg : "error";
      duration = typeof thirdArg === "number" ? thirdArg : 3000;
    }

    const { title: resolvedTitle, subText: resolvedSubText } = resolveToastContent(message, title, type, lang);

    const id = Date.now() + Math.random();
    const newToast = {
      id,
      title: resolvedTitle,
      message: resolvedSubText,
      originalMessage: message,
      type,
      isVisible: true,
    };

    setToasts(prev => [...prev, newToast]);

    timersRef.current[id] = setTimeout(() => {
      removeToast(id);
    }, duration);

    return () => {
      if (timersRef.current[id]) {
        clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];
      }
    };
  }, [removeToast, lang]);

  const value = useMemo(() => ({ toasts, showToast, removeToast }), [toasts, showToast, removeToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useCustomToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useCustomToast must be used within ToastProvider');
  }
  return context;
};

const styles = `
  .toast-card {
    width: 340px;
    min-height: 84px;
    max-height: 130px;
    border-radius: 12px;
    box-sizing: border-box;
    padding: 13px 16px;
    background-color: #ffffff;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(15, 23, 42, 0.08);
    border: 1px solid #f1f5f9;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 14px;
    animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    margin-bottom: 12px;
  }

  .toast-card.hide {
    animation: slideOut 0.3s ease-out;
  }

  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(400px);
      opacity: 0;
    }
  }

  .toast-wave {
    position: absolute;
    transform: rotate(90deg);
    left: -31px;
    top: 32px;
    width: 80px;
    fill: #ef444424;
    pointer-events: none;
  }

  .toast-wave.success {
    fill: #22c55e24;
  }

  .toast-wave.warning {
    fill: #f59e0b24;
  }

  .toast-wave.info {
    fill: #00bada24;
  }

  .toast-icon-container {
    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fef2f2;
    border-radius: 50%;
    margin-left: 2px;
    flex-shrink: 0;
  }

  .toast-icon-container.success {
    background-color: #ecfdf5;
  }

  .toast-icon-container.warning {
    background-color: #fffbeb;
  }

  .toast-icon-container.info {
    background-color: #f0fdfa;
  }

  .toast-icon {
    width: 18px;
    height: 18px;
    color: #dc2626;
  }

  .toast-icon.success {
    color: #16a34a;
  }

  .toast-icon.warning {
    color: #d97706;
  }

  .toast-icon.info {
    color: #009bb6;
  }

  .toast-message-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    flex-grow: 1;
    min-width: 0;
    gap: 3px;
  }

  .toast-message-text {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.25;
    color: #0f172a;
    letter-spacing: -0.01em;
  }

  .toast-message-text.success {
    color: #15803d;
  }

  .toast-message-text.error {
    color: #b91c1c;
  }

  .toast-message-text.warning {
    color: #b45309;
  }

  .toast-message-text.info {
    color: #0284c7;
  }

  .toast-sub-text {
    margin: 0;
    font-size: 13px;
    font-weight: 400;
    color: #64748b;
    line-height: 1.35;
    word-wrap: break-word;
    overflow-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .toast-close-icon {
    width: 18px;
    height: 18px;
    color: #94a3b8;
    cursor: pointer;
    flex-shrink: 0;
    transition: color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
  }

  .toast-close-icon:hover {
    color: #0f172a;
  }

  .toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 100005 !important;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    pointer-events: none;
  }

  .toast-card {
    pointer-events: auto;
  }
`;

export const CustomToastComponent = ({ toast, onRemove }) => {
  const [isPaused, setIsPaused] = useState(false);
  const type = toast.type || 'error';
  const isSuccess = type === 'success';
  const isWarning = type === 'warning';
  const isInfo = type === 'info';
  const isError = type === 'error';

  useEffect(() => {
    if (!isPaused && !toast.isVisible) {
      const timer = setTimeout(onRemove, 300);
      return () => clearTimeout(timer);
    }
  }, [isPaused, toast.isVisible, onRemove]);

  return (
    <>
      <style>{styles}</style>
      <div
        className={`toast-card toast-${type} ${!toast.isVisible ? 'hide' : ''}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <svg className={`toast-wave ${type}`} viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0,256L11.4,240C22.9,224,46,192,69,192C91.4,192,114,224,137,234.7C160,245,183,235,206,213.3C228.6,192,251,160,274,149.3C297.1,139,320,149,343,181.3C365.7,213,389,267,411,282.7C434.3,299,457,277,480,250.7C502.9,224,526,192,549,181.3C571.4,171,594,181,617,208C640,235,663,277,686,256C708.6,235,731,149,754,122.7C777.1,96,800,128,823,165.3C845.7,203,869,245,891,224C914.3,203,937,117,960,112C982.9,107,1006,181,1029,197.3C1051.4,213,1074,171,1097,144C1120,117,1143,107,1166,133.3C1188.6,160,1211,224,1234,218.7C1257.1,213,1280,139,1303,133.3C1325.7,128,1349,192,1371,192C1394.3,192,1417,128,1429,96L1440,64L1440,320L1428.6,320C1417.1,320,1394,320,1371,320C1348.6,320,1326,320,1303,320C1280,320,1257,320,1234,320C1211.4,320,1189,320,1166,320C1142.9,320,1120,320,1097,320C1074.3,320,1051,320,1029,320C1005.7,320,983,320,960,320C937.1,320,914,320,891,320C868.6,320,846,320,823,320C800,320,777,320,754,320C731.4,320,709,320,686,320C662.9,320,640,320,617,320C594.3,320,571,320,549,320C525.7,320,503,320,480,320C457.1,320,434,320,411,320C388.6,320,366,320,343,320C320,320,297,320,274,320C251.4,320,229,320,206,320C182.9,320,160,320,137,320C114.3,320,91,320,69,320C45.7,320,23,320,11,320L0,320Z"
            fillOpacity="1"
          />
        </svg>

        <div className={`toast-icon-container ${type}`}>
          {isSuccess && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="toast-icon success">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          )}
          {isError && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="toast-icon error">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          )}
          {isWarning && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="toast-icon warning">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          )}
          {isInfo && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="toast-icon info">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          )}
        </div>

        <div className="toast-message-container">
          <p className={`toast-message-text ${type}`}>{toast.title}</p>
          {toast.message && <p className="toast-sub-text">{toast.message}</p>}
        </div>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 15 15"
          strokeWidth="0"
          fill="none"
          stroke="currentColor"
          className={`toast-close-icon ${type}`}
          onClick={onRemove}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            fill="currentColor"
            d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
          />
        </svg>
      </div>
    </>
  );
};

export function AppToastContainer() {
  const { toasts, removeToast } = useCustomToast();
  return (
    <>
      <style>{styles}</style>
      <div className="toast-container">
        {toasts.map((toast) => (
          <CustomToastComponent
            key={toast.id}
            toast={toast}
            onRemove={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </>
  );
}

export default AppToastContainer;
